/**
 * goldenReferences.js ✦ iter207
 * ─────────────────────────────────────────────────────────────
 * Local-first persistence of "preservation truth" snapshots.
 *
 * Per Craig (iter207): "there is no single truth — there is iPhone
 * Safari truth, Android Chrome truth, kiosk tablet truth, low-light
 * truth, thermal truth. Golden references only matter if they
 * preserve environmental context."
 *
 * Each golden bundle is keyed by `(profile × deviceFingerprint)` so
 * different devices accumulate their own truth and never overwrite
 * each other.
 *
 * Storage: localStorage (browser-local, inspectable, portable).
 * The "Export bundle" path produces a downloadable JSON (visuals
 * inlined as data URLs) for offline archival in a repo or shared
 * drive.
 *
 * Bundle shape:
 *
 *   {
 *     schema: 'eventdrop.golden.v1',
 *     kind: 'composition' | 'audio',
 *     profileName,
 *     deviceFingerprint,
 *     pipelineVersion,       // iter207: lock to the pipeline rev.
 *     profileHash,           // fast equality check (FNV-1a)
 *     profileConfigSnapshot, // human-readable
 *     conditions,            // visibility / battery / thermal / etc.
 *     capturedAt,            // ISO timestamp
 *     payload: { ... }       // kind-specific contents
 *   }
 *
 * For kind:'composition' the payload carries:
 *     previewDataUrl, exportDataUrl, mirrorPreview, mirrorExport,
 *     videoSignature, dimensionsStable.
 *
 * For kind:'audio' the payload carries:
 *     rawRoomToneRMS, presenceFloorSample, peakLevelTrace,
 *     voicePresenceMoments, micDeviceLabel, recordingMime.
 */

const STORAGE_PREFIX = 'eventdrop.golden.';
const SCHEMA = 'eventdrop.golden.v1';

// Pipeline version — bump this whenever the camera/audio pipeline
// behaviour changes in a way that could shift golden truth (even
// if the profile config snapshot stays equal). Craig's iter207 rule:
// "distinguish same profile values, different pipeline behaviour."
export const PIPELINE_VERSION = 'iter207';

/**
 * Operational archaeology fields ✦ iter209. Without these, six
 * months from now you cannot answer:
 *   • did this regression begin after build X?
 *   • is this failure only happening on WebKit?
 *   • is the issue confined to a single release channel?
 *
 * `releaseChannel` and `buildSha` are read from the build-time
 * environment so they reflect the actual artifact running, not a
 * guess. `browserEngine` is derived from the UA at capture time.
 */
export function releaseChannel() {
  return (typeof process !== 'undefined'
    && process.env
    && process.env.REACT_APP_RELEASE_CHANNEL)
    || 'preview';
}

export function buildSha() {
  return (typeof process !== 'undefined'
    && process.env
    && process.env.REACT_APP_BUILD_SHA)
    || 'unknown';
}

export function browserEngine() {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent || '';
  // Order matters — Edge / Chrome on iOS still announce WebKit.
  if (/Gecko\/\d+\s+Firefox/i.test(ua)) return 'gecko';
  if (/AppleWebKit/i.test(ua) && !/Chrome|Chromium|Edg\//i.test(ua)) return 'webkit';
  if (/Chrome|Chromium|Edg\/|CriOS/i.test(ua)) return 'chromium';
  if (/AppleWebKit/i.test(ua)) return 'webkit';
  return 'unknown';
}

export function archaeology() {
  return {
    releaseChannel: releaseChannel(),
    buildSha: buildSha(),
    browserEngine: browserEngine(),
  };
}

/**
 * FNV-1a 32-bit hash — stable, deterministic, dependency-free.
 * Used to produce a `profileHash` for fast equality without
 * re-reading the full config snapshot.
 */
export function fnv1a(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return ('00000000' + h.toString(16)).slice(-8);
}

/**
 * Stable per-device fingerprint. Used to bucket golden references
 * by device so iPhone vs Android each have their own truth.
 *
 * Intentionally lo-fi — high-entropy fingerprinting belongs to
 * analytics, not preservation truth. We want a label that's stable
 * for the same physical device + browser combo, not a unique ID.
 */
export function deviceFingerprint() {
  if (typeof navigator === 'undefined') return 'unknown';
  const parts = [
    (navigator.userAgent || '').slice(0, 80),
    typeof window !== 'undefined' ? `${window.screen?.width}x${window.screen?.height}` : '',
    typeof window !== 'undefined' ? `dpr${window.devicePixelRatio || 1}` : '',
    navigator.maxTouchPoints != null ? `tp${navigator.maxTouchPoints}` : '',
    navigator.platform || '',
  ];
  return fnv1a(parts.join('|'));
}

export function keyFor(kind, profileName, fingerprint) {
  return `${STORAGE_PREFIX}${kind}.${profileName}.${fingerprint || deviceFingerprint()}`;
}

function safeGetItem(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}
function safeSetItem(key, value) {
  try { localStorage.setItem(key, value); return true; } catch { return false; }
}
function safeRemoveItem(key) {
  try { localStorage.removeItem(key); return true; } catch { return false; }
}

export function profileHash(profileConfigSnapshot) {
  return fnv1a(JSON.stringify(profileConfigSnapshot));
}

export function saveGolden({
  kind,
  profileName,
  profileConfigSnapshot,
  conditions = null,
  payload,
}) {
  const fingerprint = deviceFingerprint();
  const bundle = {
    schema: SCHEMA,
    kind,
    profileName,
    deviceFingerprint: fingerprint,
    pipelineVersion: PIPELINE_VERSION,
    profileHash: profileHash(profileConfigSnapshot),
    profileConfigSnapshot,
    conditions,
    archaeology: archaeology(),
    capturedAt: new Date().toISOString(),
    payload,
  };
  const key = keyFor(kind, profileName, fingerprint);
  safeSetItem(key, JSON.stringify(bundle));
  return { key, bundle };
}

/**
 * saveFailure ✦ iter208 — store a known-bad operational
 * archetype. The bundle is keyed by `(failureClass × profile ×
 * deviceFingerprint)` so the same failure can be captured per
 * device + per profile without collision.
 *
 * Required fields (Craig, iter208):
 *   failureClass        — from FAILURE_CLASSES
 *   severity            — 'minor' | 'major' | 'critical'
 *   signature           — buildSignature() output (telemetry truth)
 *   reproductionSteps   — operator-authored, optional but valued
 *   operatorNotes       — free-form human context
 *   evidence (optional) — { framePngDataUrl, errorMessage, ... }
 */
export function saveFailure({
  failureClass,
  severity = 'major',
  profileName,
  profileConfigSnapshot,
  conditions = null,
  signature,
  reproductionSteps = '',
  operatorNotes = '',
  capturedBy = '',
  rehearsalPackId = null,
  evidence = null,
}) {
  const fingerprint = deviceFingerprint();
  const bundle = {
    schema: SCHEMA,
    kind: 'failure',
    failureClass,
    severity,
    profileName,
    deviceFingerprint: fingerprint,
    pipelineVersion: PIPELINE_VERSION,
    profileHash: profileHash(profileConfigSnapshot),
    profileConfigSnapshot,
    conditions,
    archaeology: archaeology(),
    capturedAt: new Date().toISOString(),
    capturedBy,
    rehearsalPackId,
    signature,
    reproductionSteps,
    operatorNotes,
    evidence,
  };
  // Failures use a richer key so multiple failures of the same
  // CLASS can be stored across profiles + devices without collision.
  // We also append a short timestamp tag so an operator can capture
  // the same failure multiple times (e.g. low-light from two angles)
  // and keep both for the archetype library.
  const stamp = Date.now().toString(36);
  const key = `${STORAGE_PREFIX}failure.${failureClass}.${profileName}.${fingerprint}.${stamp}`;
  safeSetItem(key, JSON.stringify(bundle));
  return { key, bundle };
}

export function listFailures() {
  return listGoldens().filter((g) => g.bundle?.kind === 'failure');
}

export function deleteFailure(key) {
  return safeRemoveItem(key);
}

export function loadGolden(kind, profileName, fingerprint) {
  const key = keyFor(kind, profileName, fingerprint);
  const raw = safeGetItem(key);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

export function deleteGolden(kind, profileName, fingerprint) {
  const key = keyFor(kind, profileName, fingerprint);
  return safeRemoveItem(key);
}

export function listGoldens() {
  const out = [];
  if (typeof localStorage === 'undefined') return out;
  for (let i = 0; i < localStorage.length; i += 1) {
    const k = localStorage.key(i);
    if (!k || !k.startsWith(STORAGE_PREFIX)) continue;
    try {
      const v = JSON.parse(localStorage.getItem(k));
      out.push({ key: k, bundle: v });
    } catch { /* ignore corrupt entries */ }
  }
  return out;
}

/**
 * Trigger a JSON download of a golden bundle for offline archival.
 * Visuals are already inlined as data URLs in the bundle, so the
 * single JSON is self-contained.
 */
export function downloadGolden(bundle) {
  if (typeof document === 'undefined') return;
  const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `golden-${bundle.kind}-${bundle.profileName}-${bundle.deviceFingerprint}-${bundle.capturedAt.replace(/[:.]/g, '-')}.json`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Audio numeric drift report between a current measurement and a
 * stored golden bundle. Returns a structured diff the debug page
 * surfaces row-by-row.
 */
export function diffAudio(current, golden) {
  if (!current || !golden || golden.kind !== 'audio') return null;
  const g = golden.payload;
  const round = (v, p = 4) => (typeof v === 'number' ? +v.toFixed(p) : v);
  return {
    pipelineVersionDrift: current.pipelineVersion !== golden.pipelineVersion,
    profileHashDrift: current.profileHash !== golden.profileHash,
    deltas: {
      rawRoomToneRMS: round((current.rawRoomToneRMS ?? 0) - (g.rawRoomToneRMS ?? 0), 5),
      presenceFloorSample: round((current.presenceFloorSample ?? 0) - (g.presenceFloorSample ?? 0), 5),
      peakLevelMaxDelta: round(
        Math.max(...(current.peakLevelTrace || [0])) - Math.max(...(g.peakLevelTrace || [0])),
        4,
      ),
      voicePresenceMomentsCount:
        (current.voicePresenceMoments?.length || 0) - (g.voicePresenceMoments?.length || 0),
    },
    micDeviceMatch: current.micDeviceLabel === g.micDeviceLabel,
    recordingMimeMatch: current.recordingMime === g.recordingMime,
  };
}

export default {
  PIPELINE_VERSION,
  fnv1a,
  deviceFingerprint,
  keyFor,
  profileHash,
  releaseChannel,
  buildSha,
  browserEngine,
  archaeology,
  saveGolden,
  loadGolden,
  deleteGolden,
  listGoldens,
  downloadGolden,
  diffAudio,
  saveFailure,
  listFailures,
  deleteFailure,
};
